<template>
  <section class="t-left">
    <h3 class="mn-b-small">{{ t('auth.signUp.title') }}</h3>

    <!-- Tab selector -->
    <div
      v-if="availableTabs.length > 1"
      class="mn-b-small"
    >
      <Tab
        v-model:selected="tabAuth"
        :tabs="availableTabs"
        classTabBar="w-100"
      />
    </div>

    <!-- Form -->
    <div class="pos-relative">
      <!-- Phone -->
      <transition name="slide-fade">
        <div v-show="tabAuth === 'phone'" class="mn-b-semi radius-small">
          <p class="mn-b-small opacity-50">{{ t('auth.signUp.smsNotice') }}</p>
          <FieldPhone
            @change="(event) => phone = event"
            :dropdownOptions="{
              showDialCodeInSelection: true,
              showFlags: true,
              showDialCodeInList: true
            }"
            :validation="phoneValidation"
            mode="national"
            :inputOptions="{ placeholder: t('auth.signUp.phonePlaceholder') }"
            class="bg-light h-4r pd-small radius-small mn-b-thin"
          />
        </div>
      </transition>
      <!-- Email -->
      <transition name="slide-fade">
        <div v-show="tabAuth === 'email'" class="mn-b-semi radius-small o-hidden">
          <p class="mn-b-small opacity-50">{{ t('auth.signUp.emailNotice') }}</p>
          <Field
            v-model:field="email"
            :placeholder="t('auth.signUp.emailPlaceholder')"
            :validation="emailValidation"
            class="bg-light h-4r pd-medium radius-small"
          />
        </div>
      </transition>
    </div>

    <!-- Password fields -->
    <div class="mn-b-semi">
      <Field
        v-model:field="password"
        type="password"
        :placeholder="t('auth.enterPassword.passwordPlaceholder')"
        :validation="passwordValidation"
        class="bg-light pd-small radius-small mn-b-thin"
      />
      <Field
        v-model:field="passwordRepeat"
        type="password"
        :placeholder="t('auth.enterPassword.repeatPasswordPlaceholder')"
        class="bg-light pd-small radius-small"
      />
    </div>

    <!-- Agreement -->
    <div class="p-regular fw-semi mn-b-semi" v-html="t('auth.signUp.agreement')"></div>

    <!-- Button -->
    <Button :submit="onSubmit" class="w-100 button--main mn-b-big">
      {{ t('auth.enterPassword.registerBtn') }}
    </Button>

    <!-- Links -->
    <div class="w-100">
      <router-link
        :to="{ name: 'Sign In', query: { returnUrl: route.query.returnUrl } }"
        class="underline mn-b-medium d-block t-blue"
      >
        {{ t('auth.signUp.haveAccount') }}
      </router-link>
    </div>
  </section>
</template>

<script setup>
import Tab from '@martyrs/src/design-system/components/Tab/Tab.vue'
import Field from '@martyrs/src/design-system/components/Field/Field.vue'
import FieldPhone from '@martyrs/src/design-system/components/FieldPhone/FieldPhone.vue'
import Button from '@martyrs/src/design-system/components/Button/Button.vue'

import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useStore } from '@martyrs/src/modules/core/views/store/core.store.js'
import { setError } from '@martyrs/src/modules/core/views/store/core.store.js'
import { withOtp } from '@martyrs/src/modules/auth/views/plugins/otp.plugin.js'
import $axios from '@martyrs/src/modules/core/views/utils/axios-instance.js'
import * as inputsValidation from '@martyrs/src/modules/auth/views/validations/inputs.validation'

const store = useStore()
const core = store.core || { state: {} }
const auth = store.auth

const route = useRoute()
const router = useRouter()
const { t } = useI18n({ useScope: 'global' })

const email = ref('')
const phone = ref('')
const password = ref('')
const passwordRepeat = ref('')

const phoneValidation = ref(null)
const emailValidation = ref(null)
const passwordValidation = ref(null)

const availableTabs = computed(() => {
  const tabs = [
    { name: t('auth.signUp.phone'), value: 'phone' },
    { name: t('auth.signUp.email'), value: 'email' }
  ]
  const excludeMethods = core.state?.options?.auth?.authMethodsExclude || []
  return tabs.filter(tab => !excludeMethods.includes(tab.value))
})

const tabAuth = ref(availableTabs.value.length ? availableTabs.value[0].value : '')

async function onSubmit() {
  // Validate identifier
  if (tabAuth.value === 'phone') {
    await inputsValidation.validateInputs(
      phoneValidation,
      inputsValidation.validatePhone,
      phone.value,
      'Некорректный телефон'
    )
  }
  if (tabAuth.value === 'email') {
    await inputsValidation.validateInputs(
      emailValidation,
      inputsValidation.validateEmail,
      email.value,
      'Некорректный email'
    )
  }

  // Validate password
  await inputsValidation.validateInputs(
    passwordValidation,
    inputsValidation.validatePassword,
    password.value,
    'Некорректный пароль'
  )

  if (password.value !== passwordRepeat.value) {
    setError({ response: { data: { errorCode: 'PASSWORDS_DO_NOT_MATCH' } } })
    throw new Error('PASSWORDS_DO_NOT_MATCH')
  }

  // Signup with OTP verification
  try {
    const result = await withOtp(
      (data) => $axios.post('/api/auth/signup', data),
      {
        email: tabAuth.value === 'email' ? email.value : undefined,
        phone: tabAuth.value === 'phone' ? phone.value : undefined,
        password: password.value,
        type: tabAuth.value,
        inviteCode: route.query.invite
      }
    )

    // Success - save token and redirect
    if (result.data?.accessToken) {
      const Cookies = (await import('js-cookie')).default
      Cookies.set('user', JSON.stringify(result.data), { path: '/', expires: 30 })
      await auth.actions.initialize()

      if (route.query.returnUrl) {
        router.push({ path: route.query.returnUrl, query: { afterAuth: 'true' } })
      } else {
        router.push({ name: 'User Profile', params: { _id: result.data._id }, query: { afterAuth: 'true' } })
      }
    }
  } catch (error) {
    setError(error)
    throw error
  }
}
</script>

<style lang="scss">
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  position: absolute;
  transform: translateX(20px);
  opacity: 0;
  left: 0;
  top: 0;
}
</style>
